import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Building2, Loader2, MapPin, Search, Users } from "lucide-react";
import { PublicHeader } from "@/components/PublicHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Seo } from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import NotFound from "@/pages/NotFound";

const PAGE_SIZE = 24;
const initials = (n) => (n || "?").split(/\s+/).map((x) => x[0]).slice(0, 2).join("").toUpperCase();

function ProTile({ p }) {
  return (
    <Link to={p.public_profile_path} data-testid={`directory-pro-${p.public_slug}`}
      className="group flex gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition-shadow hover:border-[#C79A3B]/50 hover:shadow-md">
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-[#0F2C59]">
        {p.avatar ? <img src={p.avatar} alt={p.name} loading="lazy" className="h-full w-full object-cover object-top" />
          : <div className="flex h-full w-full items-center justify-center font-display font-bold text-[#C79A3B]">{initials(p.name)}</div>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="truncate font-semibold text-[#0F2C59]">{p.name}</span>
          {p.is_verified && <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-[#1D63D8] px-1.5 py-0.5 text-[10px] font-semibold text-white"><BadgeCheck className="h-3 w-3" /> Verified</span>}
        </div>
        <p className="truncate text-xs text-[#A9802B]">{p.sales_position_label || p.role_label}</p>
        {(p.organization_name || p.agency) && <p className="mt-1 flex items-center gap-1 truncate text-xs text-slate-500"><Building2 className="h-3 w-3 shrink-0" /><span className="truncate">{p.organization_name || p.agency}</span></p>}
        {p.city && <p className="flex items-center gap-1 truncate text-xs text-slate-500"><MapPin className="h-3 w-3 shrink-0" />{p.city}</p>}
      </div>
    </Link>
  );
}

export default function Professionals() {
  const { config } = useAuth();
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);

  useEffect(() => { setPage(1); }, [q, city]);
  useEffect(() => {
    const params = { page, limit: PAGE_SIZE };
    if (q.trim()) params.q = q.trim();
    if (city.trim()) params.city = city.trim();
    const t = setTimeout(() => {
      setData(null);
      api.get("/public/professionals", { params }).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0 }));
    }, 300);
    return () => clearTimeout(t);
  }, [q, city, page]);

  const pages = Math.max(1, Math.ceil((data?.total || 0) / PAGE_SIZE));
  if (config?.featured_professional_enabled === false) return <NotFound />; // P1 ships dark until activated
  return (
    <div className="flex min-h-screen flex-col bg-slate-50" data-testid="professionals-directory-page">
      <Seo title="Real Estate Professionals | PropertyConnect" description="Browse published real estate professional profiles across the Philippines on PropertyConnect." canonical="/professionals" robots="index,follow" />
      <PublicHeader />
      <section className="bg-[#0F2C59]">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#C79A3B]">Professional Directory</p>
          <h1 className="font-display text-3xl font-bold text-white sm:text-4xl">Real Estate Professionals</h1>
          <p className="mt-2 max-w-xl text-sm text-white/70 sm:text-base">Explore published professional profiles. Verification is shown separately on each profile.</p>
        </div>
      </section>
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="directory-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or organization…" className="rounded-xl bg-white pl-9" />
          </div>
          <div className="relative sm:w-60">
            <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="directory-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" className="rounded-xl bg-white pl-9" />
          </div>
        </div>
        {!data ? (
          <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : data.items.length === 0 ? (
          <div data-testid="directory-empty" className="py-20 text-center text-slate-500"><Users className="mx-auto mb-3 h-8 w-8 text-slate-300" />No professionals match your search.</div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="directory-grid">
            {data.items.map((p) => <ProTile key={p.public_slug} p={p} />)}
          </div>
        )}
        {data && pages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-3" data-testid="directory-pagination">
            <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-xl">Previous</Button>
            <span className="text-sm text-slate-500">Page {page} of {pages}</span>
            <Button variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} className="rounded-xl">Next</Button>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
